// Wandelt einen (bereits serverseitig normalisierten) KI-Reiseentwurf in den
// Formular-State von TripEditorPage. Zusätzliche Client-Absicherung: unbekannte
// Enum-Werte werden hier NOCHMALS gegen tripOptions gefiltert und niemals gesetzt.
import {
  TRIP_TYPES, TRANSPORT_TYPES, ACCOMMODATION_TYPES, ACTIVITIES,
} from "@/constants/tripOptions";

const valuesOf = (opts) => opts.map((o) => o.value);
const filterList = (arr, opts) => (Array.isArray(arr) ? arr.filter((v) => valuesOf(opts).includes(v)) : []);
const oneOf = (v, opts) => (valuesOf(opts).includes(v) ? v : "");

const labelMap = (opts) => Object.fromEntries(opts.map((o) => [o.value, o.label]));
const L = {
  trip: labelMap(TRIP_TYPES),
  transport: labelMap(TRANSPORT_TYPES),
  accommodation: labelMap(ACCOMMODATION_TYPES),
};
export const tripTypeLabel = (v) => L.trip[v] || v || "—";
export const transportLabels = (arr) => (arr || []).map((v) => L.transport[v] || v);
export const accommodationLabels = (arr) => (arr || []).map((v) => L.accommodation[v] || v);

// Nur erlaubte Felder werden übernommen. NIEMALS gesetzt: visibility, cover_image,
// preferred_gender, cost_sharing, preferred_age_min/max (bleiben Formular-Defaults).
export function draftToForm(d) {
  if (!d) return {};
  const form = {
    title: d.title || "",
    destination_country: d.destination_country || "",
    destination_city: d.destination_city || "",
    departure_region: d.departure_region || "",
    start_date: /^\d{4}-\d{2}-\d{2}$/.test(d.start_date || "") ? d.start_date : "",
    end_date: /^\d{4}-\d{2}-\d{2}$/.test(d.end_date || "") ? d.end_date : "",
    flexible_dates: Boolean(d.flexible_dates),
    trip_type: oneOf(d.trip_type, TRIP_TYPES),
    transport_type: filterList(d.transport_type, TRANSPORT_TYPES),
    accommodation_type: filterList(d.accommodation_type, ACCOMMODATION_TYPES),
    activities: filterList(d.activities, ACTIVITIES),
    budget_min: d.budget_min != null ? String(d.budget_min) : "",
    budget_max: d.budget_max != null ? String(d.budget_max) : "",
    companions_needed: d.companions_needed != null ? String(d.companions_needed) : "1",
    description: d.description || "",
    cover_image_suggestion: d.cover_image_suggestion || "",
    cover_image_generation_supported: !!d.cover_image_generation_supported,
  };
  // cover_image NUR wenn der Nutzer im Wizard ausdrücklich ein Bild gewählt/generiert hat.
  if (d.cover_image) form.cover_image = d.cover_image;
  return form;
}

// Menschlich lesbare Labels für die Pflichtfeld-Rückfragen (Schritt 2).
export const FIELD_LABELS = {
  title: "Reisetitel",
  destination_country: "Zielland",
  destination_city: "Reiseziel (Stadt)",
  start_date: "Startdatum",
  end_date: "Enddatum",
  trip_type: "Reiseart",
};

// ── Sitzungs-Persistenz des KI-Entwurfs ──────────────────────────────────────
// Der Entwurf bleibt bis Sitzungsende erhalten – auch über eine zwischenzeitliche
// Verifizierung hinweg (Post-Verification-Resume). Kein Backend, kein localStorage.
export const AI_DRAFT_SESSION_KEY = "voyalyo_ai_draft";
export const AI_RESUME_DISMISS_KEY = "voyalyo_ai_resume_dismissed";
export const AI_DRAFT_SCHEMA_VERSION = 2;
export const AI_DRAFT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // MVP-Aufbewahrung: 7 Tage
const DAY_MS = 24 * 60 * 60 * 1000;

const REQUIRED_FORM_FIELDS = ["title", "destination_country", "destination_city", "start_date", "end_date", "trip_type"];

export function saveSessionDraft({ prompt, draft, answers, cover_image, planning_id }) {
  try {
    // created_at über mehrere Speichervorgänge hinweg bewahren (Verfall ab Ersterstellung).
    let created_at = Date.now();
    let prevPid = "";
    try {
      const prev = JSON.parse(sessionStorage.getItem(AI_DRAFT_SESSION_KEY) || "null");
      if (prev && typeof prev.created_at === "number") created_at = prev.created_at;
      if (prev && typeof prev.planning_id === "string") prevPid = prev.planning_id;
    } catch { /* ignore */ }
    const expires_at = created_at + AI_DRAFT_RETENTION_MS;
    sessionStorage.setItem(AI_DRAFT_SESSION_KEY, JSON.stringify({
      v: AI_DRAFT_SCHEMA_VERSION, created_at, expires_at, ts: Date.now(),
      prompt, draft, answers, cover_image: cover_image || "",
      planning_id: planning_id || prevPid || "",
    }));
  } catch { /* ignore quota/private-mode */ }
}

export function readSessionDraft() {
  try {
    const raw = sessionStorage.getItem(AI_DRAFT_SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== "object" || !s.draft || typeof s.draft !== "object") return null;
    // Abgelaufen -> bereinigen und nichts zurückgeben.
    if (typeof s.expires_at === "number" && Date.now() > s.expires_at) {
      clearSessionDraft();
      return null;
    }
    return s; // { v, created_at, expires_at, ts, prompt, draft, answers }
  } catch { return null; }
}

export function clearSessionDraft() {
  try { sessionStorage.removeItem(AI_DRAFT_SESSION_KEY); } catch { /* ignore */ }
}

// Enthält der gespeicherte Entwurf alle Pflichtfelder (nach Normalisierung)?
export function isDraftComplete(formDraft) {
  return !!formDraft && REQUIRED_FORM_FIELDS.every((k) => formDraft[k]);
}

// Gespeicherten Entwurf (draft + Rückfrage-Antworten + evtl. Titelbild) in Formular-Werte überführen.
export function mergedFormFromSession(s) {
  if (!s?.draft) return null;
  return draftToForm({ ...s.draft, ...(s.answers || {}), cover_image: s.cover_image || "" });
}

// Verbleibende Gültigkeit in ms (null wenn unbekannt).
export function draftMsRemaining(s) {
  return typeof s?.expires_at === "number" ? s.expires_at - Date.now() : null;
}

export function isSessionDraftExpired(s) {
  const ms = draftMsRemaining(s);
  return ms != null && ms <= 0;
}

// Dezentes Verfügbarkeits-Label für Resume-Karte/-Dialog.
export function draftAvailabilityLabel(s) {
  const ms = draftMsRemaining(s);
  if (ms == null || ms <= 0) return null;
  if (ms < DAY_MS) return "Dieser Reiseentwurf läuft bald ab.";
  const days = Math.ceil(ms / DAY_MS);
  return `Dieser Reiseentwurf ist noch ${days} ${days === 1 ? "Tag" : "Tage"} verfügbar.`;
}
